import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Field, getIn, useFormikContext } from "formik";
import { describeExpression } from "util/CronUtils";
import usePermissions from "hooks/usePermissions";
import { Spinner } from "components/common";
import { Button, ControlLabel, Input } from "components/bootstrap";
import EventSummaryTemplateHelp from "components/event-definitions/event-definition-form/EventSummaryTemplateHelp";
import StreamsAndCategoriesFilter from "views/components/common/StreamsAndCategoriesFilter";
import { Row, Col } from "common/components";
import {
  FormikInput,
  FormikMultiSelect,
  FormikTimeUnitInput,
  FormikUserTimezoneSelect,
  FormikSearchFiltersFormControl,
  useNotificationOptions
} from "security-app/components/common/FormikComponents";
import { useGetStreams } from "security-app/hooks/useSigmaAPI";
import { EventProcedureFormField } from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureForm";
import EventProcedureSummary from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureSummary";
import { SEARCH_WITHIN_UNITS, EXECUTE_EVERY_UNITS, DEFAULT_RULE_OPTIONS } from "./utils";
const StyledHelp = styled.small`
  white-space: pre-wrap;
`;
const CheckboxRow = styled(Row)`
  & .checkbox {
    margin-top: 0;
    margin-bottom: 0;
  }
`;
const InputContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 24px;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding-right: 3px;
`;
function RuleOptionsFormFields({ fieldNamePrefix, showEventSummaryTemplate = true }) {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext();
  const [loadingForm, setLoadingForm] = useState(false);
  const { isPermitted } = usePermissions();
  const touchedFields = getIn(touched, fieldNamePrefix);
  const errorsFields = getIn(errors, fieldNamePrefix);
  const streamsRequired = !isPermitted("streams:read");
  const streamsHasErrors = streamsRequired && !!(errorsFields == null ? void 0 : errorsFields.streams);
  useEffect(() => {
    if (!getIn(values, fieldNamePrefix)) {
      setLoadingForm(true);
      setFieldValue(fieldNamePrefix, DEFAULT_RULE_OPTIONS, false).then(() => setLoadingForm(false));
    }
  }, [fieldNamePrefix, setFieldValue]);
  const options = getIn(values, fieldNamePrefix);
  const { streams, loadingStreams } = useGetStreams();
  const { notificationOptions, loadingNotifications } = useNotificationOptions();
  const [showAddEventProcedureForm, setShowAddEventProcedureForm] = useState(false);
  const handleStreamsAndCategoriesChange = (selected) => {
    Promise.all([
      setFieldValue(`${fieldNamePrefix}.streams`, selected.streams),
      setFieldTouched(`${fieldNamePrefix}.streams`, true, false)
    ]).then(() => {
      setFieldValue(`${fieldNamePrefix}.stream_categories`, selected.categories);
      setFieldTouched(`${fieldNamePrefix}.stream_categories`, true, false);
    });
  };
  const showAddNewEventProcedure = !(options == null ? void 0 : options.event_procedure_id) && !showAddEventProcedureForm;
  const showEventProcedureSummary = !!(options == null ? void 0 : options.event_procedure_id) && !showAddEventProcedureForm;
  return loadingForm ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading options forms" }) : /* @__PURE__ */ React.createElement(Col, { $gap: "md" }, showEventSummaryTemplate && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(InputContainer, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikInput,
      name: `${fieldNamePrefix}.event_summary_template`,
      id: `${fieldNamePrefix}.event_summary_template`,
      type: "text",
      label: "Event Summary Template",
      value: options == null ? void 0 : options.event_summary_template,
      help: "Template used to generate the Event and Alert summaries.",
      error: (touchedFields == null ? void 0 : touchedFields.event_summary_template) && (errorsFields == null ? void 0 : errorsFields.event_summary_template) ? errorsFields == null ? void 0 : errorsFields.event_summary_template : void 0,
      bsStyle: (touchedFields == null ? void 0 : touchedFields.event_summary_template) && (errorsFields == null ? void 0 : errorsFields.event_summary_template) ? "error" : null
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, null, /* @__PURE__ */ React.createElement(EventSummaryTemplateHelp, null)))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikSearchFiltersFormControl,
      name: `${fieldNamePrefix}.filters`,
      value: options == null ? void 0 : options.filters,
      label: "Search Filters"
    }
  )), /* @__PURE__ */ React.createElement(Row, null, loadingStreams ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading stream options" }) : /* @__PURE__ */ React.createElement(Col, { $width: "100%", $gap: "xs", $align: "stretch" }, /* @__PURE__ */ React.createElement(
    StreamsAndCategoriesFilter,
    {
      id: `${fieldNamePrefix}.streams_and_categories`,
      label: `Streams and Categories ${streamsRequired ? "*" : ""}`,
      streams,
      value: {
        streams: (options == null ? void 0 : options.streams) || [],
        categories: (options == null ? void 0 : options.stream_categories) || []
      },
      onChange: handleStreamsAndCategoriesChange,
      onBlur: () => {
        setFieldTouched(`${fieldNamePrefix}.streams`, true, false);
        setFieldTouched(`${fieldNamePrefix}.stream_categories`, true, false);
      },
      showStreams: true,
      showCategories: true,
      grouping: true,
      required: streamsRequired,
      error: streamsHasErrors ? errorsFields == null ? void 0 : errorsFields.streams : void 0
    }
  ))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(
    Field,
    {
      name: `${fieldNamePrefix}.search_within`,
      component: FormikTimeUnitInput,
      label: "Search within the last",
      value: options == null ? void 0 : options.search_within,
      units: SEARCH_WITHIN_UNITS,
      errors
    }
  )), /* @__PURE__ */ React.createElement(CheckboxRow, null, /* @__PURE__ */ React.createElement(Field, { name: `${fieldNamePrefix}.use_cron_scheduling`, type: "checkbox" }, ({ field }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      name: field.name,
      id: field.name,
      label: "Use Cron Scheduling",
      checked: field.checked,
      onChange: field.onChange
    }
  ))), /* @__PURE__ */ React.createElement(Row, null, (options == null ? void 0 : options.use_cron_scheduling) ? /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikInput,
      name: `${fieldNamePrefix}.cron_expression`,
      id: `${fieldNamePrefix}.cron_expression`,
      type: "text",
      label: "Cron Expression",
      value: options == null ? void 0 : options.cron_expression,
      help: /* @__PURE__ */ React.createElement(StyledHelp, null, describeExpression(options == null ? void 0 : options.cron_expression) || "A Quartz cron expression to determine when the event should be run."),
      error: (touchedFields == null ? void 0 : touchedFields.cron_expression) && (errorsFields == null ? void 0 : errorsFields.cron_expression) ? errorsFields == null ? void 0 : errorsFields.cron_expression : void 0,
      bsStyle: (touchedFields == null ? void 0 : touchedFields.cron_expression) && (errorsFields == null ? void 0 : errorsFields.cron_expression) ? "error" : null
    }
  ), /* @__PURE__ */ React.createElement(
    Field,
    {
      component: FormikUserTimezoneSelect,
      id: `${fieldNamePrefix}.cron_timezone`,
      name: `${fieldNamePrefix}.cron_timezone`
    }
  )) : /* @__PURE__ */ React.createElement(
    Field,
    {
      name: `${fieldNamePrefix}.execute_every`,
      component: FormikTimeUnitInput,
      label: "Execute search every",
      value: options == null ? void 0 : options.execute_every,
      units: EXECUTE_EVERY_UNITS,
      errors
    }
  )), /* @__PURE__ */ React.createElement(CheckboxRow, null, /* @__PURE__ */ React.createElement(Field, { name: `${fieldNamePrefix}.enabled`, type: "checkbox" }, ({ field }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      name: field.name,
      id: field.name,
      label: "Enable",
      help: "Should this event definition be executed automatically?",
      checked: field.checked,
      onChange: field.onChange
    }
  ))), /* @__PURE__ */ React.createElement(Row, null, loadingNotifications ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading notifications" }) : /* @__PURE__ */ React.createElement(
    Field,
    {
      name: `${fieldNamePrefix}.notifications`,
      component: FormikMultiSelect,
      options: notificationOptions,
      value: options == null ? void 0 : options.notifications,
      label: "Notifications",
      multiple: true,
      menuPlacement: "bottom"
    }
  )), /* @__PURE__ */ React.createElement(Col, { $gap: "lg" }, showAddEventProcedureForm && /* @__PURE__ */ React.createElement(Row, { $width: "100%" }, /* @__PURE__ */ React.createElement(
    Field,
    {
      name: `${fieldNamePrefix}.event_procedure_id`,
      id: `${fieldNamePrefix}.event_procedure_id`,
      value: options == null ? void 0 : options.event_procedure_id,
      component: EventProcedureFormField,
      remediationSteps: options == null ? void 0 : options.remediation_steps,
      onClose: () => setShowAddEventProcedureForm(false),
      onRemove: () => setShowAddEventProcedureForm(false)
    }
  )), showEventProcedureSummary && /* @__PURE__ */ React.createElement(Col, { $width: "100%" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedure Summary"), /* @__PURE__ */ React.createElement(
    EventProcedureSummary,
    {
      eventProcedureId: options == null ? void 0 : options.event_procedure_id,
      canEdit: true,
      onEdit: () => setShowAddEventProcedureForm(true),
      onRemove: () => {
        setFieldValue(`${fieldNamePrefix}.event_procedure_id`, "");
        setShowAddEventProcedureForm(false);
      }
    }
  )), showAddNewEventProcedure && /* @__PURE__ */ React.createElement(Col, { $gap: "xs" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedures"), /* @__PURE__ */ React.createElement("p", null, "This rule does not have any Event Procedures yet."), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowAddEventProcedureForm(true) }, "Add Event Procedure"))));
}
export default RuleOptionsFormFields;
